import { useState, useCallback } from 'react'
import { Modal, btnPrimary, btnSecondary, btnDanger, colors, input, label as labelStyle } from '../../components/ui'
import { api, ApiError } from '../../services/api'

function Field({ label, children, required }: { label: string; children: React.ReactNode; required?: boolean }) {
  return (
    <div style={{ marginBottom: '1rem' }}>
      <label style={{ ...labelStyle, display: 'block', marginBottom: '4px' }}>
        {label}
        {required && <span style={{ color: '#ef4444', marginLeft: '2px' }}>*</span>}
      </label>
      {children}
    </div>
  )
}

interface AgentTokenCreateInput {
  readonly name: string
  readonly scope: readonly string[]
  readonly allowedEnvironmentIds: string[] | null
  readonly allowedLogLevels: string[]
  readonly allowedEvtPatterns: string
  readonly maxWindowHours: number
  readonly rateLimitPerMinute: number
  readonly expiresAt: string
}

interface AgentTokenRow {
  readonly id: string
  readonly name: string
  readonly scope: readonly string[]
  readonly allowedEnvironmentIds: readonly string[] | null
  readonly allowedLogLevels: readonly string[]
  readonly allowedEvtPatterns: readonly string[]
  readonly maxWindowHours: number
  readonly rateLimitPerMinute: number
  readonly createdAt: string
  readonly expiresAt: string
  readonly revokedAt: string | null
  readonly lastUsedAt: string | null
}

interface Environment {
  readonly id: string
  readonly name: string
}

interface PlaintextModalProps {
  readonly plaintext: string
  readonly tokenName: string
  readonly onClose: () => void
}

const SCOPE_OPTIONS = [
  { value: 'fleet_logs_read', label: 'fleet_logs_read — read all fleet logs' },
  { value: 'env_logs_read', label: 'env_logs_read — read env-scoped logs' },
  { value: 'env_audit_read', label: 'env_audit_read — read audit logs' },
  { value: 'env_meta_read', label: 'env_meta_read — read env metadata' },
  { value: 'suggestions_write', label: 'suggestions_write — submit suggestions' },
  { value: 'upgrade_read', label: 'upgrade_read — read version/update status' },
]

const LOG_LEVEL_OPTIONS = ['debug', 'info', 'warn', 'error', 'fatal']

// Shown once after token creation. Operator must copy before closing.
export function PlaintextRevealModal({ plaintext, tokenName, onClose }: PlaintextModalProps) {
  const [copied, setCopied] = useState(false)
  const centralUrl = window.location.origin

  const handleCopy = useCallback(() => {
    void navigator.clipboard.writeText(plaintext)
    setCopied(true)
  }, [plaintext])

  const mcpConfig = JSON.stringify(
    {
      mcpServers: {
        'alphaedge-fleet': {
          command: 'npx',
          args: ['mcp-remote', `${centralUrl}/api/agent/mcp-tools.json`],
          env: { MCP_AUTH_TOKEN: plaintext },
        },
      },
    },
    null,
    2,
  )

  const curlSnippet = `curl -H "Authorization: Bearer ${plaintext}" ${centralUrl}/api/agent/logs?level=error`

  return (
    <Modal isOpen={true} onClose={onClose} title={`Token Created — ${tokenName}`} width="680px">
      <div style={{ padding: '1.25rem' }}>
        <div
          style={{
            background: '#fef3c7',
            border: '1px solid #f59e0b',
            borderRadius: '6px',
            padding: '0.75rem 1rem',
            marginBottom: '1.25rem',
            fontSize: '0.875rem',
            color: '#92400e',
          }}
        >
          Copy the token value now. It will NOT be shown again after closing this dialog.
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <div
            style={{
              fontSize: '0.8125rem',
              fontWeight: 600,
              color: colors.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.4px',
              marginBottom: '6px',
            }}
          >
            Token Value
          </div>
          <div
            style={{
              background: '#1e293b',
              color: '#e2e8f0',
              fontFamily: 'monospace',
              fontSize: '0.8rem',
              padding: '0.75rem',
              borderRadius: '6px',
              wordBreak: 'break-all',
              marginBottom: '0.5rem',
            }}
          >
            {plaintext}
          </div>
          <button style={{ ...btnPrimary, fontSize: '0.8125rem' }} onClick={handleCopy}>
            {copied ? 'Copied!' : 'Copy to Clipboard'}
          </button>
        </div>

        <details style={{ marginBottom: '1rem' }}>
          <summary style={{ cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 600, color: colors.textMuted }}>
            curl snippet
          </summary>
          <pre
            style={{
              background: '#1e293b',
              color: '#e2e8f0',
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              padding: '0.75rem',
              borderRadius: '6px',
              marginTop: '0.5rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
            }}
          >
            {curlSnippet}
          </pre>
        </details>

        <details style={{ marginBottom: '1.25rem' }}>
          <summary style={{ cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 600, color: colors.textMuted }}>
            MCP config block
          </summary>
          <pre
            style={{
              background: '#1e293b',
              color: '#e2e8f0',
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              padding: '0.75rem',
              borderRadius: '6px',
              marginTop: '0.5rem',
              whiteSpace: 'pre-wrap',
            }}
          >
            {mcpConfig}
          </pre>
        </details>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button style={btnSecondary} onClick={onClose}>
            I have copied the token — Close
          </button>
        </div>
      </div>
    </Modal>
  )
}

interface AgentTokenModalProps {
  readonly environments: readonly Environment[]
  readonly onClose: () => void
  readonly onCreated: (plaintext: string, tokenName: string) => void
}

export function AgentTokenModal({ environments, onClose, onCreated }: AgentTokenModalProps) {
  const [name, setName] = useState('')
  const [scope, setScope] = useState<string[]>([])
  const [allowedEnvIds, setAllowedEnvIds] = useState<string[]>([])
  const [allEnvs, setAllEnvs] = useState(false)
  const [allowedLogLevels, setAllowedLogLevels] = useState<string[]>(['info', 'warn', 'error', 'fatal'])
  const [allowedEvtPatterns, setAllowedEvtPatterns] = useState('*')
  const [maxWindowHours, setMaxWindowHours] = useState(24)
  const [rateLimitPerMinute, setRateLimitPerMinute] = useState(30)
  const [daysUntilExpiry, setDaysUntilExpiry] = useState(7)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const toggleScope = (val: string) => {
    setScope((prev) => (prev.includes(val) ? prev.filter((s) => s !== val) : [...prev, val]))
  }

  const toggleLogLevel = (val: string) => {
    setAllowedLogLevels((prev) => (prev.includes(val) ? prev.filter((l) => l !== val) : [...prev, val]))
  }

  const toggleEnvId = (id: string) => {
    setAllowedEnvIds((prev) => (prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]))
  }

  const handleSubmit = useCallback(async () => {
    if (!name.trim()) return setError('Name is required')
    if (scope.length === 0) return setError('At least one scope is required')
    if (allowedLogLevels.length === 0) return setError('At least one log level is required')
    if (daysUntilExpiry < 1 || daysUntilExpiry > 90) return setError('Expiry must be 1-90 days')

    setSaving(true)
    setError(null)

    try {
      const expiresAt = new Date(Date.now() + daysUntilExpiry * 24 * 60 * 60 * 1000).toISOString()
      const body: AgentTokenCreateInput = {
        name: name.trim(),
        scope,
        allowedEnvironmentIds: allEnvs || allowedEnvIds.length === 0 ? null : allowedEnvIds,
        allowedLogLevels,
        allowedEvtPatterns: allowedEvtPatterns.trim() || '*',
        maxWindowHours,
        rateLimitPerMinute,
        expiresAt,
      }

      const res = await api.post<{ plaintext: string; row: AgentTokenRow }>('/admin/agent-tokens', body)
      const plaintext = res.data?.plaintext
      if (!plaintext) throw new Error('No plaintext in response')
      onCreated(plaintext, name.trim())
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create token')
      setSaving(false)
    }
  }, [
    name,
    scope,
    allowedEnvIds,
    allEnvs,
    allowedLogLevels,
    allowedEvtPatterns,
    maxWindowHours,
    rateLimitPerMinute,
    daysUntilExpiry,
    onCreated,
  ])

  return (
    <Modal isOpen={true} onClose={onClose} title="Create Agent Token" width="600px">
      <div style={{ padding: '1.25rem' }}>
        {error && (
          <div
            style={{
              background: '#fee2e2',
              border: '1px solid #fca5a5',
              borderRadius: '6px',
              padding: '0.625rem 0.875rem',
              marginBottom: '1rem',
              fontSize: '0.875rem',
              color: '#991b1b',
            }}
          >
            {error}
          </div>
        )}

        <Field label="Token Name" required>
          <input
            style={input}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Claude log-analysis agent"
          />
        </Field>

        <Field label="Scope" required>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {SCOPE_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.875rem', cursor: 'pointer' }}
              >
                <input type="checkbox" checked={scope.includes(opt.value)} onChange={() => toggleScope(opt.value)} />
                <code style={{ fontSize: '0.8rem' }}>{opt.value}</code>
                <span style={{ color: colors.textMuted }}>{opt.label.split(' — ')[1]}</span>
              </label>
            ))}
          </div>
        </Field>

        <Field label="Allowed Environments">
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '0.875rem',
              cursor: 'pointer',
              marginBottom: '8px',
            }}
          >
            <input type="checkbox" checked={allEnvs} onChange={(e) => setAllEnvs(e.target.checked)} />
            All environments (no restriction)
          </label>
          {!allEnvs && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
                maxHeight: '140px',
                overflowY: 'auto',
                border: `1px solid ${colors.border}`,
                borderRadius: '4px',
                padding: '6px',
              }}
            >
              {environments.map((env) => (
                <label
                  key={env.id}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.875rem', cursor: 'pointer' }}
                >
                  <input
                    type="checkbox"
                    checked={allowedEnvIds.includes(env.id)}
                    onChange={() => toggleEnvId(env.id)}
                  />
                  {env.name}
                </label>
              ))}
              {environments.length === 0 && (
                <span style={{ color: colors.textMuted, fontSize: '0.8125rem' }}>No environments available</span>
              )}
            </div>
          )}
        </Field>

        <Field label="Allowed Log Levels">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {LOG_LEVEL_OPTIONS.map((lvl) => (
              <label
                key={lvl}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.875rem', cursor: 'pointer' }}
              >
                <input type="checkbox" checked={allowedLogLevels.includes(lvl)} onChange={() => toggleLogLevel(lvl)} />
                {lvl}
              </label>
            ))}
          </div>
        </Field>

        <Field label="Allowed Evt Patterns (comma-separated, supports * glob)">
          <input
            style={input}
            value={allowedEvtPatterns}
            onChange={(e) => setAllowedEvtPatterns(e.target.value)}
            placeholder="* or hl7.*, auth.failure"
          />
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <Field label="Max Window (hours)">
            <input
              style={input}
              type="number"
              min={1}
              max={168}
              value={maxWindowHours}
              onChange={(e) => setMaxWindowHours(Number(e.target.value))}
            />
          </Field>
          <Field label="Rate Limit (per minute)">
            <input
              style={input}
              type="number"
              min={1}
              max={300}
              value={rateLimitPerMinute}
              onChange={(e) => setRateLimitPerMinute(Number(e.target.value))}
            />
          </Field>
        </div>

        <Field label="Expires in (days, max 90)">
          <input
            style={input}
            type="number"
            min={1}
            max={90}
            value={daysUntilExpiry}
            onChange={(e) => setDaysUntilExpiry(Number(e.target.value))}
          />
        </Field>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '0.5rem' }}>
          <button style={btnDanger} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            style={btnPrimary}
            onClick={() => void handleSubmit()}
            disabled={saving || scope.length === 0 || !name.trim()}
          >
            {saving ? 'Creating…' : 'Create Token'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
